import React, { useState } from 'react';
import { FiPhone, FiSearch, FiUser, FiMapPin, FiCreditCard, FiRefreshCw, FiAlertCircle, FiCopy, FiCheck, FiEye, FiMail, FiX, FiTrash2 } from 'react-icons/fi';
import Calling from '../../api/Calling';
import formatDate from '../../components/formatDate';
import { toast } from "react-toastify";

const Mobile = () => {
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [viewRecord, setViewRecord] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Search function
    const handleSearch = async () => {
        const query = searchInput.trim();
        if (!query) {
            toast.warning("Please enter a search term", { autoClose: 2000 });
            return;
        }

        setLoading(true);
        setError(null);
        setActiveSearch(query);
        setHasSearched(true);

        try {
            const resp = await Calling.mobileGet(query);
            if (resp?.status) {
                setRecord(resp.data || null);
            } else {
                setError(resp?.message || 'Failed to fetch record');
                setRecord(null);
            }
        } catch (err) {
            setError('Something went wrong');
            setRecord(null);
        }
        setLoading(false);
    };

    // Handle Enter key
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear search
    const handleClear = () => {
        setSearchInput('');
        setActiveSearch('');
        setHasSearched(false);
        setRecord(null);
        setError(null);
    };

    // Copy to clipboard
    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        toast.success("Copied!", { autoClose: 1000 });
        setTimeout(() => setCopiedId(null), 2000);
    };

    // Delete record
    const handleDelete = async () => {
        if (!record?._id) return;
        
        setDeleteLoading(true);
        try {
            const resp = await Calling.mobileDelete({ ids: [record._id] });
            if (resp?.status) {
                toast.success("Record deleted successfully", { autoClose: 2000 });
                setDeleteModal(false);
                setViewRecord(null);
                setRecord(null);
                setHasSearched(false);
                setActiveSearch('');
                setSearchInput('');
            } else {
                toast.error(resp?.message || "Failed to delete record", { autoClose: 2000 });
            }
        } catch (err) {
            toast.error("Something went wrong", { autoClose: 2000 });
        }
        setDeleteLoading(false);
    };

    // Get operator color
    const getCircleColor = (circle) => {
        if (circle?.includes('JIO')) return 'bg-blue-100 text-blue-700 border-blue-200';
        if (circle?.includes('AIRTEL')) return 'bg-red-100 text-red-700 border-red-200';
        if (circle?.includes('VI')) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        if (circle?.includes('BSNL')) return 'bg-green-100 text-green-700 border-green-200';
        return 'bg-gray-100 text-gray-700 border-gray-200';
    };

    return (
        <div className="w-full min-h-screen bg-main-bg">
            <div className="p-4 md:p-6 md:ml-[4.4rem] pt-20 md:pt-24">

                {/* Header Card */}
                <div className="bg-main-white rounded-2xl border border-main-border p-5 md:p-6 mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Title */}
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-main-th rounded-2xl flex items-center justify-center">
                                <FiPhone className="text-main-blue text-2xl" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-main-font">Mobile Records</h1>
                            </div>
                        </div>

                        {/* Search Box */}
                        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                            <div className="relative flex-1 lg:w-80">
                                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-main-subfont w-5 h-5" />
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    placeholder="Number Search..."
                                    className="w-full pl-12 pr-4 py-3.5 border border-main-border rounded-xl focus:outline-none focus:border-main-blue focus:ring-2 focus:ring-main-blue/20 text-sm bg-main-white text-main-font placeholder-main-subfont transition-all"
                                />
                                {searchInput && (
                                    <button
                                        onClick={() => setSearchInput('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-main-subfont hover:text-main-font p-1"
                                    >
                                        <FiX className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={handleSearch}
                                disabled={loading || !searchInput.trim()}
                                className="flex items-center justify-center gap-2 px-8 py-3.5 bg-main-blue text-main-white rounded-xl hover:bg-emerald-600 transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-main-blue/20"
                            >
                                {loading ? (
                                    <FiRefreshCw className="w-5 h-5 animate-spin" />
                                ) : (
                                    <FiSearch className="w-5 h-5" />
                                )}
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="bg-main-white rounded-2xl border border-main-border">

                    {/* Active Search Bar */}
                    {hasSearched && activeSearch && (
                        <div className="p-4 border-b border-main-border bg-main-bg/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-main-subfont">Results for:</span>
                                    <span className="px-3 py-1.5 bg-main-blue/10 text-main-blue text-sm font-medium rounded-lg">
                                        "{activeSearch}"
                                    </span>
                                </div>
                                <button
                                    onClick={handleClear}
                                    className="flex items-center gap-2 px-4 py-2 text-sm text-main-subfont hover:text-main-delete hover:bg-red-50 rounded-lg transition-all"
                                >
                                    <FiX className="w-4 h-4" />
                                    Clear
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="p-5 md:p-6">

                        {/* Error */}
                        {error && (
                            <div className="flex items-center gap-3 p-4 mb-4 rounded-xl bg-red-50 border border-red-200">
                                <FiAlertCircle className="text-main-delete text-xl" />
                                <p className="text-main-delete text-sm">{error}</p>
                            </div>
                        )}

                        {/* Initial State */}
                        {!hasSearched && !loading && (
                            <div className="text-center py-20">
                                <div className="w-28 h-28 bg-gradient-to-br from-main-th to-main-thHover rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FiSearch className="text-main-blue text-5xl" />
                                </div>
                                <h2 className="text-2xl font-bold text-main-font">Search Records</h2>
                                <p className="text-main-subfont mt-2 max-w-md mx-auto">
                                    Enter a number to search the database
                                </p>
                            </div>
                        )}

                        {/* Loading */}
                        {loading && (
                            <div className="animate-pulse bg-main-bg rounded-xl p-4 border border-main-border">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-main-border rounded-full" />
                                    <div className="flex-1 space-y-3">
                                        <div className="h-4 bg-main-border rounded w-1/3" />
                                        <div className="h-3 bg-main-border rounded w-1/4" />
                                        <div className="h-3 bg-main-border rounded w-1/2" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* No Results */}
                        {hasSearched && !loading && !record && !error && (
                            <div className="text-center py-16">
                                <div className="w-20 h-20 bg-main-bg rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-main-border">
                                    <FiPhone className="text-main-subfont text-3xl" />
                                </div>
                                <h3 className="text-lg font-semibold text-main-font">No Results Found</h3>
                                <p className="text-main-subfont mt-1">
                                    Nothing found for "<span className="font-medium text-main-font">{activeSearch}</span>"
                                </p>
                                <button
                                    onClick={handleClear}
                                    className="mt-4 px-6 py-2 text-main-blue hover:bg-main-th rounded-lg font-medium transition-all"
                                >
                                    Clear Search
                                </button>
                            </div>
                        )}

                        {/* Result */}
                        {hasSearched && !loading && record && (
                            <div className="rounded-xl p-4 border border-main-border bg-main-bg hover:border-main-blue/30 transition-all">
                                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className="w-12 h-12 bg-main-th rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-main-blue font-bold text-lg">
                                                {record.m_name?.charAt(0)?.toUpperCase() || 'M'}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-main-font">{record.m_name || 'Unknown'}</h3>
                                            
                                            {record.m_fname && (
                                                <p className="text-xs text-main-subfont flex items-center gap-1 mt-0.5">
                                                    <FiUser className="w-3 h-3" />
                                                    {record.m_fname}
                                                </p>
                                            )}

                                            {record.m_email && (
                                                <button
                                                    onClick={() => handleCopy(record.m_email, `${record._id}-email`)}
                                                    className="flex items-center gap-1.5 text-xs text-main-subfont mt-1 hover:text-main-blue transition-all"
                                                >
                                                    <FiMail className="w-3 h-3" />
                                                    <span className="truncate max-w-[200px]">{record.m_email}</span>
                                                    {copiedId === `${record._id}-email` ? <FiCheck className="w-3 h-3 text-main-blue" /> : <FiCopy className="w-3 h-3" />}
                                                </button>
                                            )}

                                            {/* Phone Numbers */}
                                            <div className="flex flex-wrap gap-2 mt-3">
                                                <button
                                                    onClick={() => handleCopy(record.m_number, `${record._id}-main`)}
                                                    className="inline-flex items-center gap-2 bg-main-white px-3 py-2 rounded-lg text-sm font-mono border border-main-border hover:border-main-blue transition-all"
                                                >
                                                    <FiPhone className="w-4 h-4 text-main-blue" />
                                                    {record.m_number}
                                                    {copiedId === `${record._id}-main` ? <FiCheck className="w-4 h-4 text-main-blue" /> : <FiCopy className="w-4 h-4 text-main-subfont" />}
                                                </button>
                                                {record.m_alt_number && (
                                                    <button
                                                        onClick={() => handleCopy(record.m_alt_number, `${record._id}-alt`)}
                                                        className="inline-flex items-center gap-2 bg-main-white px-3 py-2 rounded-lg text-sm font-mono border border-main-border hover:border-main-blue transition-all"
                                                    >
                                                        <FiPhone className="w-4 h-4 text-main-subfont" />
                                                        {record.m_alt_number}
                                                        {copiedId === `${record._id}-alt` ? <FiCheck className="w-4 h-4 text-main-blue" /> : <FiCopy className="w-4 h-4 text-main-subfont" />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tags */}
                                    <div className="flex flex-wrap lg:flex-col gap-2 lg:items-end ml-0 lg:ml-0">
                                        <span className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${getCircleColor(record.m_circle)}`}>
                                            {record.m_circle || 'Unknown'}
                                        </span>
                                        {record.m_uid && (
                                            <span className="inline-flex items-center gap-1 px-2 py-1.5 bg-main-white rounded-lg text-xs font-mono border border-main-border">
                                                <FiCreditCard className="w-3 h-3 text-main-subfont" />
                                                {record.m_uid}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Address */}
                                {record.m_address && (
                                    <div className="mt-3 pt-3 border-t border-main-border/50">
                                        <p className="text-xs text-main-subfont flex items-start gap-2">
                                            <FiMapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                            <span className="line-clamp-2">{record.m_address}</span>
                                        </p>
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="flex items-center justify-between mt-3 pt-3 border-t border-main-border/50">
                                    <span className="text-xs text-main-subfont">
                                        {formatDate(record.createdAt)}
                                    </span>
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => setDeleteModal(true)}
                                            className="inline-flex items-center gap-1.5 text-xs text-main-delete hover:text-red-700 font-medium transition-all"
                                        >
                                            <FiTrash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                        <button
                                            onClick={() => setViewRecord(record)}
                                            className="inline-flex items-center gap-1.5 text-xs text-main-blue hover:text-emerald-600 font-medium transition-all"
                                        >
                                            <FiEye className="w-4 h-4" />
                                            View Details
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* View Modal */}
            {viewRecord && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setViewRecord(null)}>
                    <div className="bg-main-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="sticky top-0 bg-main-white border-b border-main-border p-5 flex items-center justify-between">
                            <h3 className="font-bold text-lg text-main-font">Record Details</h3>
                            <button onClick={() => setViewRecord(null)} className="w-9 h-9 rounded-xl bg-main-bg hover:bg-main-th flex items-center justify-center text-main-subfont hover:text-main-font transition-all">
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            {/* Profile */}
                            <div className="flex items-center gap-4 pb-4 border-b border-main-border">
                                <div className="w-16 h-16 bg-main-th rounded-full flex items-center justify-center">
                                    <span className="text-main-blue font-bold text-2xl">{viewRecord.m_name?.charAt(0)?.toUpperCase() || 'M'}</span>
                                </div>
                                <div>
                                    <h4 className="font-bold text-lg text-main-font">{viewRecord.m_name}</h4>
                                    <span className={`inline-block mt-1 px-3 py-1 rounded-lg text-xs font-medium border ${getCircleColor(viewRecord.m_circle)}`}>
                                        {viewRecord.m_circle}
                                    </span>
                                </div>
                            </div>

                            {/* Details */}
                            <div className="space-y-3">
                                <DetailRow label="Mobile" value={viewRecord.m_number} icon={<FiPhone />} copyable onCopy={handleCopy} id="modal-main" copiedId={copiedId} />
                                {viewRecord.m_alt_number && <DetailRow label="Alt Number" value={viewRecord.m_alt_number} icon={<FiPhone />} copyable onCopy={handleCopy} id="modal-alt" copiedId={copiedId} />}
                                {viewRecord.m_email && <DetailRow label="Email" value={viewRecord.m_email} icon={<FiMail />} copyable onCopy={handleCopy} id="modal-email" copiedId={copiedId} />}
                                {viewRecord.m_fname && <DetailRow label="Father's Name" value={viewRecord.m_fname} icon={<FiUser />} />}
                                {viewRecord.m_uid && <DetailRow label="UID / PAN" value={viewRecord.m_uid} icon={<FiCreditCard />} copyable onCopy={handleCopy} id="modal-uid" copiedId={copiedId} />}
                                {viewRecord.m_address && <DetailRow label="Address" value={viewRecord.m_address} icon={<FiMapPin />} />}
                            </div>

                            {/* Sub Names */}
                            {viewRecord.m_sub_name && viewRecord.m_sub_name.length > 0 && (
                                <div className="pt-4 border-t border-main-border">
                                    <span className="text-xs text-main-subfont block mb-2">Associated Names</span>
                                    <div className="flex flex-wrap gap-2">
                                        {viewRecord.m_sub_name.map((name, idx) => (
                                            <span key={idx} className="px-3 py-1.5 bg-main-bg rounded-lg text-xs font-medium text-main-font border border-main-border">
                                                {name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Timestamps */}
                            <div className="pt-4 border-t border-main-border grid grid-cols-2 gap-4">
                                <div>
                                    <span className="text-xs text-main-subfont block">Created</span>
                                    <span className="text-sm font-medium text-main-font">{formatDate(viewRecord.createdAt)}</span>
                                </div>
                                <div>
                                    <span className="text-xs text-main-subfont block">Updated</span>
                                    <span className="text-sm font-medium text-main-font">{formatDate(viewRecord.updatedAt)}</span>
                                </div>
                            </div>

                            {/* Delete Button in Modal */}
                            <div className="pt-4 border-t border-main-border">
                                <button
                                    onClick={() => {
                                        setViewRecord(null);
                                        setDeleteModal(true);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-main-delete hover:bg-red-100 rounded-xl font-medium transition-all border border-red-200"
                                >
                                    <FiTrash2 className="w-5 h-5" />
                                    Delete Record
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => !deleteLoading && setDeleteModal(false)}>
                    <div className="bg-main-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 text-center">
                            {/* Warning Icon */}
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FiTrash2 className="text-main-delete text-3xl" />
                            </div>

                            <h3 className="text-xl font-bold text-main-font mb-2">Delete Record?</h3>
                            <p className="text-main-subfont text-sm mb-2">
                                You are about to delete:
                            </p>

                            {/* Record Info */}
                            <div className="bg-main-bg rounded-xl p-4 mb-6 border border-main-border">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-main-th rounded-full flex items-center justify-center">
                                        <span className="text-main-blue font-bold">
                                            {record?.m_name?.charAt(0)?.toUpperCase() || 'M'}
                                        </span>
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-main-font">{record?.m_name || 'Unknown'}</p>
                                        <p className="text-xs text-main-subfont font-mono">{record?.m_number}</p>
                                    </div>
                                </div>
                            </div>

                            <p className="text-main-delete text-xs mb-6">
                                This action cannot be undone.
                            </p>

                            {/* Buttons */}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setDeleteModal(false)}
                                    disabled={deleteLoading}
                                    className="flex-1 px-4 py-3 bg-main-bg text-main-font rounded-xl font-medium hover:bg-main-th transition-all border border-main-border disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleteLoading}
                                    className="flex-1 px-4 py-3 bg-main-delete text-white rounded-xl font-medium hover:bg-red-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {deleteLoading ? (
                                        <>
                                            <FiRefreshCw className="w-5 h-5 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <FiTrash2 className="w-5 h-5" />
                                            Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Detail Row
const DetailRow = ({ label, value, icon, copyable, onCopy, id, copiedId }) => (
    <div className="flex items-start gap-3 p-3 bg-main-bg rounded-xl border border-main-border/50">
        <div className="w-9 h-9 bg-main-th rounded-lg flex items-center justify-center text-main-blue flex-shrink-0">
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <span className="text-xs text-main-subfont">{label}</span>
            <div className="flex items-center gap-2 mt-0.5">
                <span className="text-sm font-medium text-main-font break-all">{value}</span>
                {copyable && (
                    <button onClick={() => onCopy(value, id)} className="text-main-subfont hover:text-main-blue transition-all flex-shrink-0">
                        {copiedId === id ? <FiCheck className="w-4 h-4 text-main-blue" /> : <FiCopy className="w-4 h-4" />}
                    </button>
                )}
            </div>
        </div>
    </div>
);

export default Mobile;